/**
 * "Show your work" print block.
 *
 * Renders the CalculationExplainer steps a page already shows on screen as a
 * numbered calculation sheet for the print / PDF document, so the printed
 * record carries the rule, the numbers and the NEC citation for every step,
 * not just the inputs and answers.
 */

import type { CalculationStep } from '../components/common/CalculationExplainer';

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Numbered step list (label / formula / value / citation) as print HTML. */
export function formatCalculationSteps(steps: CalculationStep[]): string {
  if (steps.length === 0) return '<p class="calc-sheet-empty">No calculation steps.</p>';
  return `
    <ol class="calc-sheet">
      ${steps
        .map(
          (s) => `
      <li class="calc-sheet-step">
        <div class="calc-sheet-label">${escapeHtml(s.label)}</div>
        ${s.formula ? `<div class="calc-sheet-formula">${escapeHtml(s.formula)}</div>` : ''}
        ${s.value ? `<div class="calc-sheet-value">= ${escapeHtml(s.value)}</div>` : ''}
        ${s.reference ? `<div class="calc-sheet-ref">${escapeHtml(s.reference)}</div>` : ''}
        ${
          s.details && s.details.length > 0
            ? `<table class="calc-sheet-details"><tbody>${s.details
                .map((d) => `<tr${d.current ? ' class="current"' : ''}><td>${escapeHtml(d.label)}</td><td>${escapeHtml(d.value)}</td></tr>`)
                .join('')}</tbody></table>`
            : ''
        }
      </li>`
        )
        .join('')}
    </ol>
  `;
}

/** Plain-text rendering of the same steps for clipboard / text export. */
export function formatCalculationStepsText(steps: CalculationStep[]): string[] {
  return steps.map((s, i) => {
    const parts = [`${i + 1}. ${s.label}`];
    if (s.formula) parts.push(`   ${s.formula}`);
    if (s.value) parts.push(`   = ${s.value}`);
    if (s.reference) parts.push(`   [${s.reference}]`);
    return parts.join('\n');
  });
}
